/**
 * Predictive Dock Assignment Engine (Deterministic)
 *
 * Multi-criteria scoring for dock recommendations.
 * Pure logic — no side effects, no API calls, no AI language.
 *
 * Scoring breakdown:
 *   Type compatibility:  exact match +100, generic/other +20, mismatch +0
 *   No overlap:          no overlap +50, overlap -50
 *   Stacking pressure:   0 nearby +30, 1 nearby +10, 2+ nearby -10
 *   Tie-break:           lower dock number wins
 */
import { safeDateMs } from './cascadePrediction';
import { detectTemperatureMismatch, detectHandlingMismatch } from './dockCompatibilityHelpers';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DockAssignmentCandidate {
    dockId: string;
    dockNumber: string;
    dockType: string;
    score: number;
    reasons: DockAssignmentReason[];
    isRecommended: boolean;
    isEligible: boolean;
    ineligibleReason?: string;
    hasOverlap: boolean;
    /** Human-readable conflict window, e.g. '1:00 PM – 2:00 PM by ABC Carrier' */
    overlapWindow?: string;
    stackingCount: number;
}

export interface DockAssignmentReason {
    label: string;
    positive: boolean;
}

// ─── Input types (mirrors Dashboard types) ────────────────────────────────────

interface ScoringAppointment {
    id: string;
    startAt?: string | null;
    endAt?: string | null;
    trailerType?: string | null;
    status: string;
    dockId?: string | null;
    dock_id?: string | null;
}

interface ScoringDock {
    id: string;
    dockNumber: string;
    type: string;
    status?: string | null;
    // INV-4: Dock capability tags
    temperatureZone?: string | null;
    handlingCapabilities?: string | null;
}

/** INV-4: PO compatibility context for scoring */
export interface POMetadataForScoring {
    requiredTemperature?: string | null;
    handlingType?: string | null;
    /** True when linked POs have mixed/conflicting requirements */
    isMixed?: boolean;
}

/** V1.3B: Active blackout window for dock selector filtering */
export interface ActiveBlackout {
    id: string;
    dockId: string | null; // null = all docks
    reason: string;
    startAt: string;
    endAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function canonicalizeType(label?: string | null): string {
    const lower = (label || '').toLowerCase();
    if (lower.includes('reefer') || lower.includes('refrigerated')) return 'reefer';
    if (lower.includes('dry') || lower.includes('van')) return 'dry';
    if (lower.includes('flat')) return 'flatbed';
    if (lower.includes('container') || lower.includes('intermodal')) return 'container';
    return 'other';
}

// Only hard-exclude docks in maintenance/OOS/offline/closed states.
// Do NOT require 'available' — backend source-of-truth uses appointment
// overlap, not dock.status, for occupancy decisions.
const EXCLUDED_STATUSES = new Set(['maintenance', 'out_of_service', 'oos', 'offline', 'closed']);

function normalizeDockStatus(status?: string | null): string {
    return (status || '').trim().toLowerCase();
}

function isDockExcluded(normalizedStatus: string): boolean {
    return EXCLUDED_STATUSES.has(normalizedStatus) || normalizedStatus.includes('out of service');
}

function dockNumberToInt(dockNumber?: string | null): number {
    const parsed = parseInt(dockNumber || '', 10);
    return isNaN(parsed) ? Infinity : parsed;
}

function prettyType(canonical: string): string {
    switch (canonical) {
        case 'reefer': return 'Reefer';
        case 'dry': return 'Dry Van';
        case 'flatbed': return 'Flatbed';
        case 'container': return 'Container';
        default: return 'General';
    }
}

// ─── Scoring Engine ───────────────────────────────────────────────────────────

import { formatTimeInTimeZone } from './timezoneDisplay';
import { useAuthStore } from '../hooks/useAuth';

export function getRankedDockAssignments(
    appointment: ScoringAppointment,
    docks: ScoringDock[],
    todaySchedule: ScoringAppointment[],
    activeOccupants: ScoringAppointment[],
    now: number,
    /** INV-4: Optional PO metadata for compatibility scoring */
        warehouseTimeZone?: string,
    /** INV-4: Optional PO metadata for compatibility scoring */
    poMetadata?: POMetadataForScoring,
    /** V1.3B: Active blackout windows - docks with overlapping blackouts are ineligible */
    activeBlackouts?: ActiveBlackout[],
): DockAssignmentCandidate[] {
    if (docks.length === 0) return [];
    
    

    const occupiedDockIds = new Set<string>();
    for (const occ of activeOccupants) {
        const did = occ.dockId || occ.dock_id;
        if (did) occupiedDockIds.add(did);
    }

    const apptType = canonicalizeType(appointment.trailerType);
    const apptStartMs = safeDateMs(appointment.startAt);
    const DEFAULT_DURATION_MS = 60 * 60 * 1000; // 60 min fallback
    const apptEndMs = !isNaN(safeDateMs(appointment.endAt))
        ? safeDateMs(appointment.endAt)
        : (!isNaN(apptStartMs) ? apptStartMs + DEFAULT_DURATION_MS : NaN);

    // Score each dock
    const candidates = docks.map((dock): DockAssignmentCandidate => {
        const status = normalizeDockStatus(dock.status);
        const reasons: DockAssignmentReason[] = [];

        // ── Hard-exclude only maintenance/OOS/offline/closed ──
        if (isDockExcluded(status)) {
            return {
                dockId: dock.id,
                dockNumber: dock.dockNumber,
                dockType: dock.type,
                score: -1000,
                reasons: [],
                isRecommended: false,
                isEligible: false,
                ineligibleReason: 'Maintenance / Out of Service',
                hasOverlap: false,
                stackingCount: 0,
            };
        }

        // ── V1.3B: Hard-exclude blacked-out docks ──
        if (activeBlackouts && activeBlackouts.length > 0) {
            const blackout = activeBlackouts.find(b => {
                // Match specific dock or warehouse-wide (dockId=null)
                if (b.dockId !== null && b.dockId !== dock.id) return false;
                // Check time overlap with appointment or current time
                const bStart = safeDateMs(b.startAt);
                const bEnd = safeDateMs(b.endAt);
                if (isNaN(bStart) || isNaN(bEnd)) return false;
                // Use appointment window if available, else current time
                const checkStart = !isNaN(apptStartMs) ? apptStartMs : now;
                const checkEnd = !isNaN(apptEndMs) ? apptEndMs : now + 3600000;
                return checkStart < bEnd && checkEnd > bStart;
            });
            if (blackout) {
                const fmtTime = (ms: number) => formatTimeInTimeZone(new Date(ms).toISOString(), warehouseTimeZone);
                const bStart = safeDateMs(blackout.startAt);
                const bEnd = safeDateMs(blackout.endAt);
                return {
                    dockId: dock.id,
                    dockNumber: dock.dockNumber,
                    dockType: dock.type,
                    score: -1000,
                    reasons: [],
                    isRecommended: false,
                    isEligible: false,
                    ineligibleReason: `Blocked: ${blackout.reason}`,
                    overlapWindow: `${fmtTime(bStart)} – ${fmtTime(bEnd)}`,
                    hasOverlap: true,
                    stackingCount: 0,
                };
            }
        }

        let score = 0;

        // ── 1. Type compatibility ──
        const dockType = canonicalizeType(dock.type);
        if (apptType === dockType && apptType !== 'other') {
            score += 100;
            reasons.push({ label: `${prettyType(apptType)} match`, positive: true });
        } else if (apptType === 'other' || dockType === 'other') {
            score += 20;
            reasons.push({ label: 'General dock', positive: true });
        } else {
            reasons.push({ label: `${prettyType(dockType)} dock`, positive: false });
        }

        // ── 1b. Temperature compatibility (INV-4) ──
        if (poMetadata && !poMetadata.isMixed && poMetadata.requiredTemperature) {
            const dockTz = (dock.temperatureZone || '').toLowerCase().trim();
            if (dockTz) {
                if (dockTz === 'mixed' || dockTz === 'mixed-use') {
                    score += 40;
                    reasons.push({ label: 'Mixed-use dock', positive: true });
                } else if (!detectTemperatureMismatch(dock.temperatureZone, poMetadata.requiredTemperature)) {
                    score += 80;
                    reasons.push({ label: 'Temperature match', positive: true });
                } else {
                    score -= 60;
                    reasons.push({ label: 'Temperature mismatch', positive: false });
                }
            }
        } else if (poMetadata?.isMixed) {
            reasons.push({ label: 'Mixed PO requirements', positive: false });
        }

        // ── 1c. Handling compatibility (INV-4) ──
        if (poMetadata && !poMetadata.isMixed && poMetadata.handlingType) {
            const dockCaps = (dock.handlingCapabilities || '').trim();
            if (dockCaps) {
                if (!detectHandlingMismatch(dock.handlingCapabilities, poMetadata.handlingType)) {
                    score += 40;
                    reasons.push({ label: 'Handling supported', positive: true });
                } else {
                    score -= 30;
                    reasons.push({ label: 'Handling mismatch', positive: false });
                }
            }
        }

        // ── 2. Real time-window overlap check (merged schedule + active occupants) ──
        // Dedupe: combine todaySchedule + activeOccupants
        const seen = new Set<string>();
        const merged: ScoringAppointment[] = [];
        for (const a of [...todaySchedule, ...activeOccupants]) {
            if (!seen.has(a.id)) { seen.add(a.id); merged.push(a); }
        }

        let hasOverlap = false;
        let overlapWindow: string | undefined;
        if (!isNaN(apptStartMs) && !isNaN(apptEndMs)) {
            for (const sched of merged) {
                if (sched.id === appointment.id) continue;
                const schedDockId = sched.dockId || sched.dock_id;
                if (schedDockId !== dock.id) continue;
                const schedStatus = (sched.status || '').toLowerCase();
                if (schedStatus === 'declined' || schedStatus === 'cancelled' || schedStatus === 'completed') continue;

                const schedStartMs = safeDateMs(sched.startAt);
                if (isNaN(schedStartMs)) continue;
                const schedEndMs = !isNaN(safeDateMs(sched.endAt))
                    ? safeDateMs(sched.endAt)
                    : schedStartMs + DEFAULT_DURATION_MS;
                if (isNaN(schedEndMs)) continue;

                if (apptStartMs < schedEndMs && apptEndMs > schedStartMs) {
                    hasOverlap = true;
                    const fmt = (ms: number) => formatTimeInTimeZone(new Date(ms).toISOString(), warehouseTimeZone);
                    overlapWindow = `${fmt(schedStartMs)} – ${fmt(schedEndMs)}`;
                    break;
                }
            }
        }

        if (hasOverlap) {
            score -= 50;
            reasons.push({ label: 'Overlap risk', positive: false });
        } else {
            score += 50;
            reasons.push({ label: 'No overlap', positive: true });
        }

        // ── 2b. Active occupant penalty (soft, not hard-fail) ──
        const isOccupied = occupiedDockIds.has(dock.id);
        if (isOccupied && !hasOverlap) {
            // Occupied but no time-window overlap — warn but don't reject
            score -= 15;
            // If appointment is future-dated (not same day), show reassuring label
            const appointmentDay = !isNaN(apptStartMs) ? new Date(apptStartMs).toDateString() : '';
            const todayStr = new Date(now).toDateString();
            if (appointmentDay && appointmentDay !== todayStr) {
                reasons.push({ label: 'Occupied now • Free at requested time', positive: true });
            } else {
                reasons.push({ label: 'Currently occupied', positive: false });
            }
        }

        // ── 3. Stacking pressure (next 2 hours) ──
        let stackingCount = 0;
        const twoHoursMs = 2 * 60 * 60 * 1000;
        for (const sched of merged) {
            if (sched.id === appointment.id) continue;
            const schedDockId = sched.dockId || sched.dock_id;
            if (schedDockId !== dock.id) continue;
            const schedStatus = (sched.status || '').toLowerCase();
            if (schedStatus === 'declined' || schedStatus === 'cancelled' || schedStatus === 'completed') continue;

            const schedStartMs = safeDateMs(sched.startAt);
            if (isNaN(schedStartMs)) continue;

            if (schedStartMs >= now && schedStartMs < now + twoHoursMs) {
                stackingCount++;
            }
        }

        if (stackingCount === 0) {
            score += 30;
            reasons.push({ label: 'Low congestion', positive: true });
        } else if (stackingCount === 1) {
            score += 10;
        } else {
            score -= 10;
            reasons.push({ label: 'Higher load', positive: false });
        }

        return {
            dockId: dock.id,
            dockNumber: dock.dockNumber,
            dockType: dock.type,
            score,
            reasons: reasons.slice(0, 4), // Cap at 4 reason tags (INV-4: +1 for compatibility)
            isRecommended: false,
            isEligible: true,
            hasOverlap,
            overlapWindow,
            stackingCount,
        };
    });

    // Sort: eligible first, then by score desc, then dock number asc
    candidates.sort((a, b) => {
        if (a.isEligible !== b.isEligible) return a.isEligible ? -1 : 1;
        if (b.score !== a.score) return b.score - a.score;
        const numA = dockNumberToInt(a.dockNumber);
        const numB = dockNumberToInt(b.dockNumber);
        if (numA !== numB) return numA - numB;
        return a.dockId.localeCompare(b.dockId);
    });

    // Mark top eligible dock WITHOUT overlap as recommended.
    // If all docks overlap, no dock gets recommended.
    const bestNoOverlap = candidates.find(c => c.isEligible && !c.hasOverlap);
    if (bestNoOverlap) {
        bestNoOverlap.isRecommended = true;
    }

    return candidates;
}
